import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { useAuthStore } from '../../stores/authStore.js';

export function ProtectedRoute({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const status = useAuthStore((s) => s.status);
  const mustChange = useAuthStore((s) => s.user?.mustChangePassword ?? false);
  const location = useLocation();

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center text-copy text-muted-foreground">
        {t('common.loading', 'Loading…')}
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  // Force-rotation gate: an authenticated user with mustChangePassword=true
  // can only see /change-password until they rotate. The flag is set by
  // bulk import / admin reset / installer seed; cleared by the change-
  // password endpoint. Without this redirect they'd see the rest of the
  // app with a known-compromised password (one that an admin/operator
  // has seen in plaintext or that ships as Admin123!).
  if (mustChange && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}
